import type { MouseEvent as ReactMouseEvent, PointerEvent as ReactPointerEvent, Ref } from 'react';
import clsx from 'clsx';
import { Icon } from '@/components/Icon';
import { resolveIcon } from '@/components/iconSrc';
import { displayName } from '@/fs/icons';
import { fsApi } from '@/fs/useFs';
import { dropTargetProps } from '@/dnd/drop';
import type { DropKind } from '@/dnd/drop';
import type { FsNode } from '@/fs/types';
import css from './Desktop.module.css';

const ICON_SIZE = 48;

export interface DesktopIconProps {
  node: FsNode;
  selected: boolean;
  /** Overrides the node's own icon. The Recycle Bin uses it to look full. */
  icon?: string;
  /** Set when this icon will take a drop: a folder, a shortcut to one, the bin. */
  drop?: { kind: DropKind; path: string };
  /** The surface this icon belongs to, for the drop it might accept. */
  owner: string;
  ref?: Ref<HTMLButtonElement>;
  onPointerDown(node: FsNode, event: ReactPointerEvent<HTMLButtonElement>): void;
  onSelect(node: FsNode): void;
  onOpen(node: FsNode): void;
  onContextMenu(node: FsNode, event: ReactMouseEvent<HTMLButtonElement>): void;
}

export function DesktopIcon({
  node,
  selected,
  icon,
  drop,
  owner,
  ref,
  onPointerDown,
  onSelect,
  onOpen,
  onContextMenu,
}: DesktopIconProps) {
  const src = icon ?? fsApi.iconFor(node);
  const label = displayName(node);
  // The mask has to be the same picture `Icon` ends up drawing. Callers name
  // an icon by its SVG, but at 48px that resolves to the extracted XP bitmap,
  // whose silhouette is its own — mask with the SVG and the blue lands on a
  // shape the icon underneath no longer has.
  const maskSrc = resolveIcon(src, ICON_SIZE).src;

  function handleActivate(event: ReactMouseEvent<HTMLButtonElement>) {
    // Enter/Space on a focused button report detail 0; a real click never does.
    // That keeps single-click as select-only while still opening from the
    // keyboard, without hand-rolling key handling on top of a <button>.
    if (event.detail === 0) onOpen(node);
  }

  function handleContextMenu(event: ReactMouseEvent<HTMLButtonElement>) {
    // Windows selected whatever you right-clicked before showing its menu. The
    // pointer route already did that on pointerdown; this covers Shift+F10 and
    // the Menu key, which never touch the pointer at all.
    onSelect(node);
    onContextMenu(node, event);
  }

  return (
    <button
      ref={ref}
      type="button"
      className={clsx(css.icon, selected && css.iconSelected)}
      tabIndex={0}
      title={node.comment}
      draggable={false}
      {...(drop ? dropTargetProps(drop.kind, owner, drop.path) : null)}
      onPointerDown={(event) => onPointerDown(node, event)}
      onFocus={() => onSelect(node)}
      onClick={handleActivate}
      onDoubleClick={() => onOpen(node)}
      onContextMenu={handleContextMenu}
    >
      <span className={css.glyph}>
        <Icon src={src} size={ICON_SIZE} />
        {selected && (
          <span
            className={css.tint}
            style={{ WebkitMaskImage: `url("${maskSrc}")`, maskImage: `url("${maskSrc}")` }}
            aria-hidden="true"
          />
        )}
      </span>
      <span className={css.label}>{label}</span>
    </button>
  );
}

export { ICON_SIZE as DESKTOP_ICON_SIZE };
